import React, { useEffect, useState } from 'react';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, formatDate, logout } from '../../utils';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';
import useResetSubmittingOnRestore from '../../hooks/useResetSubmittingOnRestore';

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    fetchUserInfo('/event-select', setUserInfo, setLoading, setError);
  }, []);

  // Keep the event cards clickable after the browser restores this page from the
  // back/forward cache (otherwise the `submitting` lock stays stuck). See the hook.
  useResetSubmittingOnRestore(setSubmitting);

  const handleEventSelect = async (eventId) => {
    if (submitting) return;
    try {
      setSubmitting(eventId);
      const response = await fetch('/api/event-select', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          credentials: 'include',
        },
        body: JSON.stringify({ event_id: eventId }),
      });
      if (!response.ok) throw new Error('Failed to select event');
      await response.json();
      // Crew must pick which battle/activity they're scoring before the
      // dashboard makes sense (it keys off battle_id); other roles go home.
      window.location.href = userInfo?.role === 'crew' ? '/battle-select' : '/home';
    } catch (err) {
      console.error('Error selecting event:', err);
      setSubmitting(null);
    }
  };

  return (
    <Screen>
      <div className="top">
        <div className="event-select-shell">
          <div className="event-select-header">
            <Back to="/role" />
            <h2 className="event-select-header__title">{t('selectYourEvent')}</h2>
            <p className="event-select-header__sub">{t('selectYourEventSub')}</p>
            <button
              className="event-select-header__logout"
              onClick={logout}
              aria-label={t('logout')}
            >
              <Icon name="sign-out-alt" theme="primary" />
            </button>
          </div>

          {userInfo?.events?.length > 0 ? (
            <div className="event-list">
              {userInfo.events.map((event) => {
                const isSubmitting = submitting === event.id;
                return (
                  <Card
                    key={event.id}
                    mx="0"
                    mb="2"
                    className={`event-card ${isSubmitting ? 'event-card--loading' : ''}`}
                    onClick={() => handleEventSelect(event.id)}
                  >
                    <div className="event-card__row">
                      <Image
                        size="4.5rem"
                        className="event-card__image"
                        url={event.image_link ? event.image_link : require('./../../assets/images/fallback/event.png')}
                      />
                      <div className="event-card__body">
                        <div className="event-card__name">{event.name}</div>
                        <div className="event-card__date">
                          <Icon name="calendar" theme="primary" className="me-1" />
                          {formatDate(event.date)}
                        </div>
                      </div>
                      <Icon name="angle-right" className="event-card__chevron" />
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <NotFound translationKey="noEventsFound" loading={loading} />
          )}
        </div>
      </div>
    </Screen>
  );
};

export default Page;
